// ============================================================================
// Tenant quota configuration and measured usage view models
// ============================================================================

/**
 * The eight quota limits persisted by `tenant_quotas`.
 *
 * These identifiers are frontend view-model keys. They are deliberately kept
 * separate from the backend field names so a route path can never receive a
 * display slug such as `api_calls` by accident.
 */
export type ResourceType =
  | "users"
  | "api_keys_per_user"
  | "strategies"
  | "orders"
  | "api_calls"
  | "websocket"
  | "storage"
  | "historical_data_days";

/** Exact unit/window represented by a persisted quota field. */
export type QuotaPeriod =
  "current" | "per_user" | "minute" | "daily" | "retention";

export type QuotaAlertSeverity = "info" | "warning" | "critical";
export type QuotaAlertState = "firing" | "acknowledged_required" | "resolved";

/** A configured limit. Only `limit` is mutable through the current API. */
export interface ResourceQuota {
  resourceType: ResourceType;
  backendField:
    | "maxUsers"
    | "maxApiKeysPerUser"
    | "maxStrategies"
    | "maxOrdersPerDay"
    | "maxApiRequestsPerMinute"
    | "maxWebsocketConnections"
    | "maxStorageBytes"
    | "maxHistoricalDataDays";
  limit: number;
  period: QuotaPeriod;
  unit: string;
}

/** Threshold evidence returned by the alert FSM. */
export interface QuotaThreshold {
  percentage: number;
  severity: QuotaAlertSeverity;
}

/**
 * A measured counter paired with a configured limit.
 *
 * `null` means the rollup does not contain an authoritative observation for
 * that resource. It must never be rendered or evaluated as a measured zero.
 */
export interface ResourceUsage {
  resourceType: ResourceType;
  currentUsage: number | null;
  quota: ResourceQuota;
  percentageUsed: number | null;
  lastUpdated: Date | null;
  trend: "increasing" | "decreasing" | "stable" | "unavailable";
  projectedOverage: Date | null;
}

export interface UsageDataPoint {
  timestamp: Date;
  value: number;
}

export interface UsageHistory {
  resourceType: ResourceType;
  dataPoints: UsageDataPoint[];
  average: number;
  peak: number;
  peakTimestamp: Date;
}

export interface QuotaAlert {
  id: string;
  tenantId: string;
  /** Historical rows can contain a retired resource identifier. */
  resourceType: string;
  threshold: QuotaThreshold;
  state: QuotaAlertState;
  triggeredAt: Date;
  currentUsage: number;
  quota: number;
  acknowledged: boolean;
  acknowledgedAt?: Date;
  acknowledgedBy?: string;
  resolvedAt?: Date;
}

/** Exact configuration represented by the backend quota row. */
export interface TenantQuotaConfig {
  tenantId: string;
  quotas: ResourceQuota[];
  createdAt: Date;
  updatedAt: Date;
}

export interface UsageSummary {
  tenantId: string;
  dataAvailable: boolean;
  periodStart: Date | null;
  periodEnd: Date | null;
  totalResources: number;
  resourcesAtRisk: number;
  resourcesCritical: number;
  resourcesOverLimit: number;
  lastCalculated: Date | null;
  usageByResource: ResourceUsage[];
}

export interface TenantQuotaResetResult {
  tenantId: string;
  resetAt: Date;
  resolvedAlerts: number;
  refreshed: boolean;
}

export interface QuotaComparisonResult {
  resourceType: ResourceType;
  currentLimit: number;
  recommendedLimit: number;
  reason: string;
  priority: "low" | "medium" | "high";
}

export interface ResourceTypeInfo {
  type: ResourceType;
  label: string;
  description: string;
  icon: string;
  unit: string;
  color: string;
  periodLabel: string;
}

export const RESOURCE_TYPE_INFO: Record<ResourceType, ResourceTypeInfo> = {
  users: {
    type: "users",
    label: "Users",
    description: "Tenant user memberships",
    icon: "users",
    unit: "users",
    color: "#ec4899",
    periodLabel: "Current total",
  },
  api_keys_per_user: {
    type: "api_keys_per_user",
    label: "API Keys per User",
    description: "Maximum API keys that one user may hold",
    icon: "key",
    unit: "keys",
    color: "#a855f7",
    periodLabel: "Per user",
  },
  strategies: {
    type: "strategies",
    label: "Strategies",
    description: "Configured trading strategies",
    icon: "chart-line",
    unit: "strategies",
    color: "#14b8a6",
    periodLabel: "Current total",
  },
  orders: {
    type: "orders",
    label: "Orders",
    description: "Orders admitted per day",
    icon: "shopping-cart",
    unit: "orders",
    color: "#f59e0b",
    periodLabel: "Per day",
  },
  api_calls: {
    type: "api_calls",
    label: "API Requests",
    description: "API requests admitted per minute",
    icon: "server",
    unit: "requests",
    color: "#8b5cf6",
    periodLabel: "Per minute",
  },
  websocket: {
    type: "websocket",
    label: "WebSocket Connections",
    description: "Concurrent WebSocket connections",
    icon: "wifi",
    unit: "connections",
    color: "#3b82f6",
    periodLabel: "Concurrent",
  },
  storage: {
    type: "storage",
    label: "Storage",
    description: "Persisted tenant storage",
    icon: "database",
    unit: "bytes",
    color: "#6366f1",
    periodLabel: "Current total",
  },
  historical_data_days: {
    type: "historical_data_days",
    label: "Historical Data Retention",
    description: "Maximum retained history window",
    icon: "calendar",
    unit: "days",
    color: "#06b6d4",
    periodLabel: "Retention",
  },
};

/** Detector policy returned as alert evidence; it is not tenant-editable. */
export const DEFAULT_QUOTA_THRESHOLDS: readonly QuotaThreshold[] = [
  { percentage: 70, severity: "info" },
  { percentage: 80, severity: "warning" },
  { percentage: 95, severity: "critical" },
];

/**
 * Calculate a comparable utilization percentage.
 *
 * A zero/non-finite limit or invalid measurement is unavailable rather than a
 * fabricated zero percent. Values above 100 are preserved.
 */
export function calculateUsagePercentage(
  used: number,
  limit: number,
): number | null {
  if (
    !Number.isFinite(limit) ||
    limit <= 0 ||
    !Number.isFinite(used) ||
    used < 0
  ) {
    return null;
  }
  return (used / limit) * 100;
}

export function getUsageAlertSeverity(
  percentage: number | null,
): QuotaAlertSeverity | null {
  if (percentage === null || !Number.isFinite(percentage)) {
    return null;
  }
  if (percentage > 95) {
    return "critical";
  }
  if (percentage > 80) {
    return "warning";
  }
  if (percentage > 70) {
    return "info";
  }
  return null;
}
